import { createContext, useContext, useState, type ReactNode } from 'react'

export type Size = '2xs' | 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl'

export type CrosswordColors = {
  normalBg: string
  normalBorder: string
  normalText: string
  invertedBg: string
  invertedBorder: string
  invertedText: string
  horizontalHighlightBg: string
  horizontalHighlightBorder: string
  verticalHighlightBg: string
  verticalHighlightBorder: string
  conflictBg: string
}

const DEFAULT_COLORS: CrosswordColors = {
  normalBg: '#ffffff',
  normalBorder: '#1e2025',
  normalText: '#1e2025',
  invertedBg: '#2D3442',
  invertedBorder: '#1e2025',
  invertedText: '#ffffff',
  horizontalHighlightBg: '#fef3c7',
  horizontalHighlightBorder: '#fbbf24',
  verticalHighlightBg: '#e0f2fe',
  verticalHighlightBorder: '#38bdf8',
  conflictBg: 'rgb(220 38 38 / 6%)',
}

type CrosswordSettingsValue = {
  size: Size
  setSize: (size: Size) => void
  colors: CrosswordColors
  setColors: (colors: CrosswordColors) => void
  updateColor: (key: keyof CrosswordColors, value: string) => void
  resetColors: () => void
  showDebug: boolean
  setShowDebug: (show: boolean) => void
}

const CrosswordSettingsContext = createContext<CrosswordSettingsValue | null>(null)

export function CrosswordSettingsProvider({ children }: { children: ReactNode }) {
  const [size, setSize] = useState<Size>('base')
  const [colors, setColors] = useState<CrosswordColors>(DEFAULT_COLORS)
  const [showDebug, setShowDebug] = useState(false)

  const updateColor = (key: keyof CrosswordColors, value: string) => {
    setColors(prev => ({ ...prev, [key]: value }))
  }

  const resetColors = () => {
    setColors(DEFAULT_COLORS)
  }

  return (
    <CrosswordSettingsContext.Provider value={{
      size,
      setSize,
      colors,
      setColors,
      updateColor,
      resetColors,
      showDebug,
      setShowDebug,
    }}>
      {children}
    </CrosswordSettingsContext.Provider>
  )
}

export function useCrosswordSettings() {
  const context = useContext(CrosswordSettingsContext)
  if (!context) {
    throw new Error('useCrosswordSettings must be used within CrosswordSettingsProvider')
  }
  return context
}

export { DEFAULT_COLORS }
